import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import DataTable from "../../components/common/DataTable";
import { MoreHorizontal } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Menu,
  Image,
  Modal,
  TextInput,
  FileInput,
  Stack,
  Group,
} from "@mantine/core";

import {
  Check,
  Pencil,
  PlayCircle,
  Trash,
  UserMinus,
  UserPlus,
  X,
} from "lucide-react";
import React from "react";
import ReactPlayer from "react-player";
import { CSVLink } from "react-csv";
import { Link } from "react-router-dom";
import {
  addCastToVerticalSeries,
  deleteVerticalSeries,
  removeVerticalSeriesCast,
  updateVerticalSeries,
  updateVerticalSeriesCast,
} from "../../utils/api";

const VerticalSeriesTable = ({
  data,
  loading,
  reload,
  permissions = {},
  pagination,
  currentPage,
  exportCsvData,
  exportCsvFilename,
}) => {
  const [isAddCastModalVisible, setAddCastModalVisible] = React.useState(false);
  const [selectedSeriesId, setSelectedSeriesId] = React.useState("");
  const [castData, setCastData] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [isUpdateCastModalVisible, setUpdateCastModalVisible] =
    React.useState(false);
  const [selectedCastForUpdate, setSelectedCastForUpdate] =
    React.useState(null);
  const [updateCastData, setUpdateCastData] = React.useState({});
  const [updateCastImageFile, setUpdateCastImageFile] = React.useState(null);

  const [videoModalVisible, setVideoModalVisible] = React.useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = React.useState("");
  const [removeCastModalVisible, setRemoveCastModalVisible] =
    React.useState(false);
  const [selectedSeriesForRemoveCast, setSelectedSeriesForRemoveCast] =
    React.useState(null);

  const [addCastImageFile, setAddCastImageFile] = React.useState(null);

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isEnable }) => {
      return updateVerticalSeries({
        id,
        data: { isEnable },
      });
    },
    onSuccess: () => {
      notifications.show({
        message: "Short Drama status updated successfully",
        color: "green",
      });
      reload();
    },
    onError: () => {
      notifications.show({
        message: "Failed to update Short Drama status",
        color: "red",
      });
    },
  });

  const updateCastMutation = useMutation({
    mutationFn: ({ data }) => updateVerticalSeriesCast(data),
    onSuccess: () => {
      notifications.show({
        message: "Cast updated successfully",
        color: "green",
      });
      setUpdateCastModalVisible(false);
      setUpdateCastData({});
      setUpdateCastImageFile(null);
      setSelectedCastForUpdate(null);
      reload();
    },
    onError: () => {
      notifications.show({ message: "Failed to update cast", color: "red" });
    },
  });

  const removeCastMutation = useMutation({
    mutationFn: (data) => removeVerticalSeriesCast(data),
    onSuccess: () => {
      notifications.show({
        message: "Cast removed successfully",
        color: "green",
      });
      setRemoveCastModalVisible(false);
      setSelectedSeriesForRemoveCast(null);
      reload();
    },
    onError: () => {
      notifications.show({ message: "Failed to remove cast", color: "red" });
    },
  });

  const handleToggleStatus = (record) => {
    toggleStatusMutation.mutate({
      id: record._id,
      isEnable: !record.isEnable,
    });
  };

  const openVideoModal = (url) => {
    setCurrentVideoUrl(url);
    setVideoModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      const [success] = await deleteVerticalSeries(record._id);
      if (success) {
        notifications.show({
          message: "Short Drama deleted successfully",
          color: "green",
        });
        reload();
      }
    } catch (error) {
      notifications.show({
        message: "Failed to delete Short Drama",
        color: "red",
      });
    }
  };

  const handleAddCast = async () => {
    if (!castData.name || !castData.role) {
      notifications.show({
        message: "Please fill all cast details",
        color: "red",
      });
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      if (addCastImageFile) {
        formData.append("image", addCastImageFile);
      }
      formData.append("name", castData.name);
      formData.append("role", castData.role);

      const [success] = await addCastToVerticalSeries(
        selectedSeriesId,
        formData,
      );

      if (success) {
        notifications.show({ message: "Cast added successfully", color: "green" });
        setAddCastModalVisible(false);
        setCastData({});
        setAddCastImageFile(null);
        reload();
      }
    } catch (error) {
      notifications.show({ message: "Failed to add cast", color: "red" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCast = async () => {
    if (!updateCastData.name || !updateCastData.role) {
      notifications.show({
        message: "Please fill all cast details",
        color: "red",
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("castId", selectedCastForUpdate._id);
      formData.append("seriesId", selectedSeriesId);
      formData.append("name", updateCastData.name);
      formData.append("role", updateCastData.role);

      if (updateCastImageFile) {
        formData.append("image", updateCastImageFile);
      }

      updateCastMutation.mutate({ data: formData });
    } catch (error) {
      notifications.show({ message: "Failed to update cast", color: "red" });
    }
  };

  const openUpdateCastModal = (cast, seriesId) => {
    setSelectedCastForUpdate(cast);
    setSelectedSeriesId(seriesId);
    setUpdateCastImageFile(null);
    setUpdateCastData({
      name: cast.name,
      role: cast.role,
    });
    setUpdateCastModalVisible(true);
  };

  const columns = [
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, flexDirection: "column" }}>
          <Menu shadow="md" width={200} trigger="click">
            <Menu.Target>
              <Button><MoreHorizontal size={16} /></Button>
            </Menu.Target>
            <Menu.Dropdown>
              {permissions?.updateVerticalSeries && (
                <Menu.Item leftSection={<Pencil size={16} />} component={Link} to={`/vertical-series/edit/${record._id}?page=${currentPage}`}>
                  Edit
                </Menu.Item>
              )}
              <Menu.Item leftSection={<PlayCircle size={16} />} component={Link} to={`/vertical-episodes/${record._id}`}>
                Episodes
              </Menu.Item>
              {permissions?.deleteVerticalSeries && (
                <Menu.Item
                  color="red"
                  leftSection={<Trash size={16} />}
                  onClick={() => modals.openConfirmModal({
                    title: "Are you sure you want to delete this Short Drama?",
                    labels: { confirm: "Yes", cancel: "No" },
                    onConfirm: () => handleDelete(record),
                  })}
                >
                  Delete
                </Menu.Item>
              )}
              {permissions?.updateVerticalSeries && (
                <Menu.Item
                  leftSection={<UserPlus size={16} />}
                  onClick={() => {
                    setAddCastModalVisible(true);
                    setSelectedSeriesId(record._id);
                    setCastData({});
                    setAddCastImageFile(null);
                  }}
                >
                  Add Cast
                </Menu.Item>
              )}
              {permissions?.updateVerticalSeries && record?.cast?.length > 0 && (
                <Menu.Item
                  leftSection={<UserMinus size={16} />}
                  onClick={() => {
                    setSelectedSeriesForRemoveCast(record);
                    setRemoveCastModalVisible(true);
                  }}
                >
                  Remove Cast
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </div>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "isEnable",
      key: "isEnable",
      render: (isEnable) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 5,
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            border: "1px solid ",
            borderColor: isEnable ? "#00ff0050" : "#ff000050",
            backgroundColor: isEnable ? "#00ff0050" : "#ff000050",
          }}
        >
          {isEnable ? <Check size={16} /> : <X size={16} />}
        </div>
      ),
    },
    {
      title: "Status Action",
      key: "statusAction",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Button
            color="red"
            variant="filled"
            onClick={() => handleToggleStatus(record)}
            loading={toggleStatusMutation.isPending}
            style={{ width: "100%" }}
            disabled={!permissions?.isPublishAccess}
          >
            {!record.isEnable ? "Enable" : "Disable"}
          </Button>
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <div style={{ width: 300, overflow: "hidden" }}>
          {text?.slice(0, 100)}
        </div>
      ),
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
    },
    {
      title: "Genres",
      dataIndex: "genres",
      key: "genres",
      render: (genres) =>
        genres?.map((item) => (
          <span key={item.name || item._id}>{item.name || item}, </span>
        )),
    },
    {
      title: "Trailer",
      dataIndex: "trailer",
      key: "trailer",
      render: (url) =>
        url ? (
          <Button variant="subtle" onClick={() => openVideoModal(url)}>
            Play Trailer
          </Button>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Cast",
      dataIndex: "cast",
      key: "cast",
      render: (cast, record) => (
        <div>
          {cast?.map((item) => (
            <div
              key={item._id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "4px",
              }}
            >
              <span>
                {item.name} ({item.role})
              </span>
              {permissions?.updateVerticalSeries && (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => openUpdateCastModal(item, record._id)}
                  style={{ padding: 0, height: "auto" }}
                >
                  <Pencil size={12} />
                </Button>
              )}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Banner With Title",
      dataIndex: "bannerWithTitleImage",
      key: "bannerWithTitleImage",
      render: (url) =>
        url ? <Image src={url} width={100} height={100} alt="banner" /> : "N/A",
    },
    {
      title: "Title Image",
      dataIndex: "titleImage",
      key: "titleImage",
      render: (url) =>
        url ? <Image src={url} width={100} height={100} alt="title" /> : "N/A",
    },
    {
      title: "Banner Image",
      dataIndex: "bannerImage",
      key: "bannerImage",
      render: (url) =>
        url ? <Image src={url} width={100} height={100} alt="banner" /> : "N/A",
    },
  ];

  const csvData = (exportCsvData || data || []).map((row) => {
    const csvRow = {
      name: row.name || "",
      description: row.description || "",
      isEnable: row.isEnable ? "Yes" : "No",
      language: row.language || "",
      genres: row.genres?.map((g) => g.name || g).join(", ") || "",
      ageLimit: row.ageLimit || "",
      releaseDate: row.releaseDate
        ? new Date(row.releaseDate).toLocaleDateString()
        : "",
      bannerWithTitleImage: row.bannerWithTitleImage || "",
      titleImage: row.titleImage || "",
      bannerImage: row.bannerImage || "",
      stripEnable: row.stripEnable ? "Yes" : "No",
      stripText: row.stripText || "",
      status: row.status || "",
      isBanner: row.isBanner ? "Yes" : "No",
      bannerIndex: row.bannerIndex || "",
      isPopular: row.isPopular ? "Yes" : "No",
      popularIndex: row.popularIndex || "",
      isUpdatedRecently: row.isUpdatedRecently ? "Yes" : "No",
      updatedRecentlyIndex: row.updatedRecentlyIndex || "",
      isMoreShows: row.isMoreShows ? "Yes" : "No",
      moreShowIndex: row.moreShowIndex || "",
      isHotShow: row.isHotShow ? "Yes" : "No",
      hotShowIndex: row.hotShowIndex || "",
      isNewShow: row.isNewShow ? "Yes" : "No",
      newShowIndex: row.newShowIndex || "",
      isOriginalShow: row.isOriginalShow ? "Yes" : "No",
      originalShowIndex: row.originalShowIndex || "",
      cast: row.cast?.map((c) => `${c.name} (${c.role})`).join("; ") || "",
    };

    // Explicitly exclude trailer and url fields
    // These should never be included in the export

    return csvRow;
  });

  return (
    <>
      {exportCsvData && exportCsvFilename && (
        <div className="flex justify-end mb-4">
          <CSVLink data={csvData} filename={exportCsvFilename} target="_blank">
            <Button>Export as CSV</Button>
          </CSVLink>
        </div>
      )}
      <DataTable
        rowKey={(record) => record._id}
        scroll={{ x: 1000 }}
        dataSource={data?.reverse()}
        loading={loading}
        columns={columns}
        pagination={pagination}
      />

      {/* Add Cast Modal */}
      <Modal
        opened={isAddCastModalVisible}
        onClose={() => {
          setAddCastModalVisible(false);
          setCastData({});
          setAddCastImageFile(null);
        }}
        title="Add Cast"
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            placeholder="Enter cast name"
            required
            value={castData.name || ""}
            onChange={(e) =>
              setCastData((prev) => ({ ...prev, name: e.target.value }))
            }
          />
          <TextInput
            label="Role"
            placeholder="Enter cast role"
            required
            value={castData.role || ""}
            onChange={(e) =>
              setCastData((prev) => ({ ...prev, role: e.target.value }))
            }
          />
          <FileInput
            label="Image"
            placeholder="Upload image"
            accept="image/*"
            clearable
            value={addCastImageFile}
            onChange={setAddCastImageFile}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setAddCastModalVisible(false);
                setCastData({});
                setAddCastImageFile(null);
              }}
            >
              Cancel
            </Button>
            <Button loading={isLoading} onClick={handleAddCast}>
              Add
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Update Cast Modal */}
      <Modal
        opened={isUpdateCastModalVisible}
        onClose={() => {
          setUpdateCastModalVisible(false);
          setUpdateCastData({});
          setUpdateCastImageFile(null);
          setSelectedCastForUpdate(null);
        }}
        title="Update Cast"
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            placeholder="Enter cast name"
            required
            value={updateCastData.name || ""}
            onChange={(e) =>
              setUpdateCastData((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
          />
          <TextInput
            label="Role"
            placeholder="Enter cast role"
            required
            value={updateCastData.role || ""}
            onChange={(e) =>
              setUpdateCastData((prev) => ({
                ...prev,
                role: e.target.value,
              }))
            }
          />
          <FileInput
            label="New image (optional)"
            placeholder="Upload new image"
            accept="image/*"
            clearable
            value={updateCastImageFile}
            onChange={setUpdateCastImageFile}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setUpdateCastModalVisible(false);
                setUpdateCastData({});
                setUpdateCastImageFile(null);
                setSelectedCastForUpdate(null);
              }}
            >
              Cancel
            </Button>
            <Button
              loading={updateCastMutation.isPending}
              onClick={handleUpdateCast}
            >
              Update
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Video Modal */}
      <Modal
        opened={videoModalVisible}
        onClose={() => {
          setVideoModalVisible(false);
          setCurrentVideoUrl("");
        }}
        size="xl"
        title="Trailer"
      >
        <div style={{ width: "100%", height: "450px" }}>
          <ReactPlayer
            url={currentVideoUrl}
            width="100%"
            height="100%"
            controls
            playing
          />
        </div>
      </Modal>

      {/* Remove Cast Modal */}
      <Modal
        opened={removeCastModalVisible}
        onClose={() => {
          setRemoveCastModalVisible(false);
          setSelectedSeriesForRemoveCast(null);
        }}
        title="Remove Cast"
      >
        <Stack gap="sm">
          {selectedSeriesForRemoveCast?.cast?.map((castMember) => (
            <Group
              key={castMember._id}
              justify="space-between"
              p="xs"
              style={{
                border: "1px solid var(--mantine-color-default-border)",
                borderRadius: 4,
              }}
            >
              <span>
                {castMember.name} ({castMember.role})
              </span>
              <Button
                variant="subtle"
                color="red"
                leftSection={<Trash size={16} />}
                loading={removeCastMutation.isPending}
                onClick={() =>
                  modals.openConfirmModal({
                    title:
                      "Are you sure you want to remove this cast member?",
                    labels: { confirm: "Yes", cancel: "No" },
                    onConfirm: () => {
                      removeCastMutation.mutate({
                        seriesId: selectedSeriesForRemoveCast._id,
                        castId: castMember._id,
                      });
                    },
                  })
                }
              >
                Remove
              </Button>
            </Group>
          ))}
        </Stack>
      </Modal>
    </>
  );
};

export default VerticalSeriesTable;
